/**
 * Layer Types and Unified ID Convention
 *
 * All layer IDs follow pattern: {groupId}-{sublayerType}
 *
 * Examples for layer with uuid "abc123":
 *   groupId = "layer-abc123"
 *
 *   Source IDs:
 *     layer-abc123-source          (main GeoJSON source)
 *     layer-abc123-raster-source   (raster imagery source)
 *
 *   Layer IDs:
 *     layer-abc123-fill            (polygon fill)
 *     layer-abc123-line            (line features)
 *     layer-abc123-point           (point features)
 *     layer-abc123-polygon-outline (polygon outlines)
 *     layer-abc123-raster          (raster imagery)
 *     layer-abc123-symbol          (labels/icons)
 *     layer-abc123-geometry        (boundary outline)
 */

import type { Map as MapboxMap } from 'mapbox-gl'
import type { Ref } from 'vue'

// Layer type enum matching backend
export type LayerType = 0 | 1 | 2 | 3 | 4 | 5

export const LayerTypeLabels: Record<LayerType, string> = {
  0: 'PlanetScope',
  1: 'Order Scene',
  2: 'Planetary Variables',
  3: 'Radar',
  4: 'Project Data',
  5: 'Static'
}

// Standard sublayer order within a group (bottom to top)
export const SUBLAYER_ORDER = [
  'fill',
  'raster',
  'line',
  'polygon-outline',
  'point',
  'circle',
  'symbol',
  'geometry'
] as const

export type SublayerType = typeof SUBLAYER_ORDER[number]

// Layer configuration passed to variants
export interface LayerConfig {
  type: LayerType
  groupId: string
  beforeId?: string
  isVisible: boolean
  orgId: number
}

// Base props that all layer variants receive
export interface LayerVariantProps {
  map: MapboxMap
  groupId: string
  beforeId?: string
  isVisible: boolean
  orgId: number
  code: string
  layer: Record<string, any>
}

// Layer group info for the manager
export interface LayerGroupInfo {
  groupId: string
  sublayers: string[]
  beforeGroupId?: string
}

/**
 * Create a unified layer ID
 * @param groupId - The layer group ID (e.g., "layer-abc123")
 * @param sublayerType - The sublayer type (e.g., "point", "line", "raster")
 * @returns Unified layer ID (e.g., "layer-abc123-point")
 */
export function createLayerId(groupId: string, sublayerType: SublayerType | string): string {
  return `${groupId}-${sublayerType}`
}

/**
 * Create a unified source ID
 * @param groupId - The layer group ID (e.g., "layer-abc123")
 * @param sourceType - The source type (default: "source")
 * @returns Unified source ID (e.g., "layer-abc123-source")
 */
export function createSourceId(groupId: string, sourceType: string = 'source'): string {
  return `${groupId}-${sourceType}`
}

/**
 * Create a group ID from a layer UUID
 * @param uuid - The layer UUID
 * @returns Group ID (e.g., "layer-abc123")
 */
export function createGroupId(uuid: string): string {
  return `layer-${uuid}`
}

/**
 * Extract UUID from a group ID
 * @param groupId - The group ID (e.g., "layer-abc123")
 * @returns The UUID (e.g., "abc123") or null if invalid
 */
export function extractUuidFromGroupId(groupId: string): string | null {
  if (groupId.startsWith('layer-')) {
    return groupId.slice(6)
  }
  return null
}

/**
 * Check if a layer ID belongs to a group
 * @param layerId - The layer ID to check
 * @param groupId - The group ID
 * @returns True if the layer belongs to the group
 */
export function isLayerInGroup(layerId: string, groupId: string): boolean {
  return layerId.startsWith(`${groupId}-`)
}

/**
 * Get the sublayer type from a layer ID
 * @param layerId - The full layer ID (e.g., "layer-abc123-point")
 * @param groupId - The group ID (e.g., "layer-abc123")
 * @returns The sublayer type (e.g., "point") or null if not found
 */
export function getSublayerType(layerId: string, groupId: string): string | null {
  if (!layerId.startsWith(`${groupId}-`)) {
    return null
  }
  return layerId.slice(groupId.length + 1)
}

// Default layer used as base reference for ordering
export const DEFAULT_LAYER_ID = 'default-layer'
